import React, { useState } from 'react';
import _get from 'lodash/get';
import StarRating from 'components/Rating';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import _classNames from 'classnames';
import Numeral from 'numeral';
import Tooltip from '@material-ui/core/Tooltip';
import DarkGrayInfoIcon from 'components/ItemPageScoring/images/dark-gray-info.svg';
import InfoIcon from 'components/UserAndExpertRating/images/info.svg';
import GrayInfoIcon from 'components/UserAndExpertRating/images/gray-info.svg';
import useSiteCopySelector from 'components/useSiteCopySelector';

const useStyles = makeStyles(theme => ({
  wrapper: {
    marginTop: 10,
  },
  header: {
    ...theme.typography.pxSmall,
    fontFamily: 'MadaBold',
  },
  body: {
    ...theme.typography.pxSmall,
  },
  ratingWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  rating: {
    ...theme.typography.pxSmall,
    fontSize: 10,
    color: theme.palette.text.primary,
    marginLeft: 4,
  },
  count: {
    color: theme.palette.text.secondary,
  },
  iconWrapper: {
    display: 'inherit',
    cursor: 'pointer',
  },
  icon: {
    height: 10,
    width: 10,
  },
  bigIcon: {
    height: 17,
    width: 17,
  },
}));

const useTooltipStyles = makeStyles(() =>
  createStyles({
    tooltip: {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 2px 14px 0 rgba(0, 0, 0, 0.05)',
      border: `1px solid #E9ECED`,
      borderRadius: 5,
      width: 234,
      padding: '10px 15px',
    },
    popper: {
      zIndex: 8,
    },
    arrow: {
      fontSize: 20,
      '&::before': {
        border: `1px solid #E9ECED`,
        backgroundColor: '#FFFFFF',
        boxSizing: 'border-box',
      },
    },
  }),
);

function RatingTooltip({ big, expert, user, userCount, slug }) {
  const classes = useStyles();
  const tooltipClasses = useTooltipStyles();
  const [isHovered, setHovered] = useState(false);
  const [opened, setOpened] = useState(false);

  const [ratingSiteCopy] = useSiteCopySelector(['small-resource-card-rating']);

  const handleInfoClick = () => setOpened(!opened);
  const handleClose = () => setOpened(false);

  const content = (
    <Grid container>
      <Grid item xs={12}>
        <Grid container>
          <Grid item>
            <Typography
              align="left"
              color="textPrimary"
              className={classes.header}
            >
              {_get(
                ratingSiteCopy,
                'pageCopy.user-rating-label',
                'User Rating',
              )}
            </Typography>
          </Grid>
          <Grid item xs className={classes.ratingWrapper}>
            <StarRating name={`${slug}-user-rating`} value={user} isSmall />
            <Typography className={classes.rating}>{`${Numeral(user).format(
              '0.0',
            )}`}</Typography>
            {userCount > 0 && (
              <Typography
                variant="subtitle2"
                className={_classNames(classes.rating, classes.count)}
              >
                (<u>{`${Numeral(userCount).format('0a')}`}</u>)
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography
              align="left"
              color="textPrimary"
              className={classes.body}
            >
              {_get(ratingSiteCopy, 'pageCopy.user-rating-description', '')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.wrapper}>
        <Grid container>
          <Grid item xs={6}>
            <Typography
              align="left"
              color="textPrimary"
              className={classes.header}
            >
              {_get(
                ratingSiteCopy,
                'pageCopy.expert-rating-label',
                'Expert Rating',
              )}
            </Typography>
          </Grid>
          <Grid item xs={6} className={classes.ratingWrapper}>
            <StarRating name={`${slug}-expert-rating`} value={expert} isSmall />
            <Typography className={classes.rating}>{`${Numeral(expert).format(
              '0.0',
            )}`}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              align="left"
              color="textPrimary"
              className={classes.body}
            >
              {_get(ratingSiteCopy, 'pageCopy.expert-rating-description', '')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <>
      <Hidden smDown>
        <Tooltip
          classes={tooltipClasses}
          placement="top-start"
          onOpen={() => setHovered(true)}
          onClose={() => setHovered(false)}
          arrow
          title={content}
          PopperProps={{
            modifiers: { flip: { enabled: false } },
          }}
        >
          <img
            src={
              isHovered || opened
                ? InfoIcon
                : big
                ? DarkGrayInfoIcon
                : GrayInfoIcon
            }
            className={big ? classes.bigIcon : classes.icon}
            alt=""
          />
        </Tooltip>
      </Hidden>
      <Hidden mdUp>
        <ClickAwayListener onClickAway={handleClose}>
          <Tooltip
            classes={tooltipClasses}
            placement="top-end"
            open={opened}
            arrow
            title={content}
            PopperProps={{
              modifiers: { flip: { enabled: false } },
            }}
          >
            <div
              onClick={handleInfoClick}
              role="button"
              tabIndex={0}
              className={classes.iconWrapper}
            >
              <img
                src={
                  isHovered ? InfoIcon : big ? DarkGrayInfoIcon : GrayInfoIcon
                }
                className={big ? classes.bigIcon : classes.icon}
                alt="Info"
              />
            </div>
          </Tooltip>
        </ClickAwayListener>
      </Hidden>
    </>
  );
}

RatingTooltip.propTypes = {
  big: PropTypes.bool,
  expert: PropTypes.number,
  slug: PropTypes.string,
  user: PropTypes.number,
  userCount: PropTypes.number,
};

export default RatingTooltip;
